import { useEffect, useRef } from 'react';

interface HelmetProps {
  children?: React.ReactNode;
}

export function Helmet({ children }: HelmetProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const elements: HTMLElement[] = [];
    container.childNodes.forEach((node) => {
      if (node instanceof Element) {
        elements.push(node as HTMLElement);
      }
    });

    const createdElements: HTMLElement[] = [];

    elements.forEach((el) => {
      const tagName = el.tagName.toLowerCase();

      if (tagName === 'title') {
        document.title = el.textContent || '';
      } else if (tagName === 'meta') {
        const name = el.getAttribute('name');
        const property = el.getAttribute('property');
        const existingMeta = name
          ? document.querySelector(`meta[name="${name}"]`)
          : property
            ? document.querySelector(`meta[property="${property}"]`)
            : null;

        if (existingMeta) {
          existingMeta.setAttribute('content', el.getAttribute('content') || '');
        } else {
          const newMeta = document.createElement('meta');
          Array.from(el.attributes).forEach((attr) => {
            newMeta.setAttribute(attr.name, attr.value);
          });
          document.head.appendChild(newMeta);
          createdElements.push(newMeta);
        }
      } else if (tagName === 'link') {
        const rel = el.getAttribute('rel');
        const existingLink = rel
          ? document.querySelector(`link[rel="${rel}"]`)
          : null;

        if (existingLink) {
          existingLink.setAttribute('href', el.getAttribute('href') || '');
        } else {
          const newLink = document.createElement('link');
          Array.from(el.attributes).forEach((attr) => {
            newLink.setAttribute(attr.name, attr.value);
          });
          document.head.appendChild(newLink);
          createdElements.push(newLink);
        }
      } else if (tagName === 'script') {
        const newScript = document.createElement('script');
        newScript.type = 'application/ld+json';
        newScript.textContent = el.textContent || '';
        document.head.appendChild(newScript);
        createdElements.push(newScript);
      }
    });

    return () => {
      createdElements.forEach((el) => el.remove());
    };
  }, [children]);

  return <div ref={containerRef} style={{ display: 'none' }}>{children}</div>;
}